import React, { useCallback, useEffect, useState } from 'react';
import CustomModal from '../../../util_components/custom_modal';
import { get_api_url } from '../../../../utils/urls';
import { get_api, post_api } from '../../../../redux/api_funcs';
import {
  earnings_reason_error,
  last_change_text,
  single_payload
} from '../../teacher_payments/overview/earnings_logic';

// PROD-7828 R1 payment-attributes card; grant-only module, so a 403 on load hides the card entirely.
const FIELD_STYLES = {
  card: {
    background: 'var(--surface-card)',
    border: '1px solid var(--border-default)',
    borderRadius: 8,
    padding: '12px 16px',
    margin: '12px 0'
  },
  label: {
    fontSize: 11,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: 0.5
  },
  value: { fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' },
  fieldLabel: { fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)' },
  input: { display: 'block', width: '100%', padding: 7, marginTop: 4 }
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// '2026-06-09' -> '9 Jun 2026'. Split by hand: the value is a zoneless IST date, and Date() would shift it.
export const format_live_day = (day) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day || '');
  return m ? `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}` : '';
};

// A half-typed <input type="date"> reports '', so only an explicit "clear" may send no date.
export const live_date_to_send = ({ profile_live_at, clear }) => {
  if (clear) return { date: '' };
  if (!/^\d{4}-\d{2}-\d{2}$/.test(profile_live_at || '')) {
    return { error: 'Enter a complete date, or tick "Clear the live date" to remove it.' };
  }
  return { date: profile_live_at };
};

// Today's India date as 'YYYY-MM-DD' (the server refuses later live dates).
export const today_ist = (now = new Date()) =>
  new Date(now.getTime() + 330 * 60 * 1000).toISOString().slice(0, 10);

const PaymentAttributes = ({ teacher_uuid, on_saved }) => {
  const [data, setData] = useState(null); // { attrs, can_edit }
  const [error, setError] = useState('');
  const [forbidden, setForbidden] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');
  const [liveForm, setLiveForm] = useState(null); // { profile_live_at, clear, reason } while the live-date editor is open
  const [earningsForm, setEarningsForm] = useState(null); // { visible, reason } while the Earnings confirm is open

  const load = useCallback(() => {
    setError('');
    const url = get_api_url(
      `/v2/cms_user/teacher_payments/teacher_attrs?teacher_uuid=${teacher_uuid}`,
      false,
      true
    );
    get_api(url, true)
      .then((res) => {
        // malformed envelope must surface the error state, not hide the card forever
        if (!res?.data?.data) throw new Error('unexpected response shape');
        setData(res.data.data);
      })
      .catch((e) => {
        if (e?.response?.status === 403) {
          setForbidden(true);
          return;
        }
        console.error('payment_attributes load failed', e);
        setError('Failed to load payment attributes');
      });
  }, [teacher_uuid]);

  useEffect(() => {
    // reset before loading so a teacher switch never shows the previous teacher's attrs
    setData(null);
    setError('');
    setForbidden(false);
    if (teacher_uuid) load();
  }, [teacher_uuid, load]);

  if (!teacher_uuid || forbidden) return null;
  if (error) {
    return (
      <div style={FIELD_STYLES.card}>
        <span style={{ color: 'var(--state-error)', fontSize: 12, marginRight: 10 }}>{error}</span>
        <button
          type="button"
          onClick={load}
          style={{
            padding: '3px 10px',
            border: '1px solid var(--border-default)',
            background: 'var(--surface-card)',
            borderRadius: 5,
            fontSize: 11,
            cursor: 'pointer',
            color: 'var(--text-secondary)'
          }}
        >
          Retry
        </button>
      </div>
    );
  }
  if (!data) {
    return (
      <div style={FIELD_STYLES.card}>
        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
          Loading payment attributes…
        </span>
      </div>
    );
  }

  const { attrs, can_edit, extra_participant_category: extraParticipantCategory } = data;

  const openEdit = () => {
    setForm({
      is_gst_registered: attrs.is_gst_registered,
      free_pass_balance: String(attrs.free_pass_balance),
      reason: ''
    });
    setModalError('');
    setEditing(true);
  };

  const closeModal = () => {
    if (!saving) setEditing(false);
  };

  // client-side diff — mirrors the backend's no-op semantics so we can skip empty saves
  const formChanged = () => {
    if (Number(form.is_gst_registered) !== attrs.is_gst_registered) return true;
    if (form.free_pass_balance !== '' && Number(form.free_pass_balance) !== attrs.free_pass_balance)
      return true;
    return false;
  };

  const save = () => {
    setModalError('');
    if (!formChanged()) {
      setEditing(false);
      return;
    }
    if (form.free_pass_balance !== '') {
      const n = Number(form.free_pass_balance);
      if (!Number.isInteger(n) || n < 0 || n > 12) {
        setModalError('Free Pass balance must be a whole number between 0 and 12.');
        return;
      }
    }
    if (!form.reason.trim()) {
      setModalError('A reason is required — it goes to the payment audit trail.');
      return;
    }
    setSaving(true);
    // The backend refuses addl_participant_teacher_type / training_batch_date (the category comes
    // from Profile Live At, edited separately) — never sent.
    const payload = {
      teacher_uuid,
      is_gst_registered: Number(form.is_gst_registered),
      reason: form.reason.trim()
    };
    // blank balance = leave unchanged (never coerce '' to 0 — that would silently zero it)
    if (form.free_pass_balance !== '') {
      payload.free_pass_balance = Number(form.free_pass_balance);
    }
    const url = get_api_url('/v2/cms_user/teacher_payments/teacher_attrs', false, true);
    post_api(url, payload, true)
      .then(() => {
        setEditing(false);
        load();
        if (on_saved) on_saved();
      })
      .catch((e) => {
        setModalError(e?.response?.data?.reason || 'Failed to save payment attributes');
      })
      .finally(() => setSaving(false));
  };

  const openLiveEdit = () => {
    setLiveForm({
      profile_live_at: extraParticipantCategory?.profile_live_at || '',
      clear: false,
      reason: ''
    });
    setModalError('');
  };

  const closeLiveEdit = () => {
    if (!saving) setLiveForm(null);
  };

  const saveLiveDate = () => {
    setModalError('');
    const { date, error: dateError } = live_date_to_send(liveForm);
    if (dateError) {
      setModalError(dateError);
      return;
    }
    if ((extraParticipantCategory?.profile_live_at || '') === date) {
      setLiveForm(null);
      return;
    }
    if (!liveForm.reason.trim()) {
      setModalError('A reason is required — it goes to the payment audit trail.');
      return;
    }
    setSaving(true);
    const url = get_api_url(
      '/v2/cms_user/teacher_payments/teacher_attrs/profile_live_at',
      false,
      true
    );
    // '' clears the date (the server then uses the profile's created date; neither date = Old)
    post_api(url, { teacher_uuid, profile_live_at: date, reason: liveForm.reason.trim() }, true)
      .then(() => {
        setLiveForm(null);
        load();
        if (on_saved) on_saved();
      })
      .catch((e) => {
        setModalError(e?.response?.data?.reason || 'Failed to save the profile live date');
      })
      .finally(() => setSaving(false));
  };

  // A-15: the teacher app's Earnings page, on or off per teacher (audited; no payment change).
  const openEarnings = () => {
    setEarningsForm({ visible: Number(attrs.earnings_visible) !== 1, reason: '' });
    setModalError('');
  };

  const closeEarnings = () => {
    if (!saving) setEarningsForm(null);
  };

  const saveEarnings = () => {
    setModalError('');
    const reasonError = earnings_reason_error(earningsForm.reason);
    if (reasonError) {
      setModalError(reasonError);
      return;
    }
    setSaving(true);
    const url = get_api_url('/v2/cms_user/teacher_payments/teacher_attrs', false, true);
    post_api(url, single_payload(teacher_uuid, earningsForm.visible, earningsForm.reason), true)
      .then(() => {
        setEarningsForm(null);
        load();
        if (on_saved) on_saved();
      })
      .catch((e) => {
        setModalError(e?.response?.data?.reason || 'Failed to change Earnings visibility');
      })
      .finally(() => setSaving(false));
  };

  const earningsKnown = attrs.earnings_visible !== undefined; // absent on an older backend
  const earningsOn = Number(attrs.earnings_visible) === 1;
  const staffAccount = data.earnings_staff_account === true;
  const earningsVerb = earningsForm && earningsForm.visible ? 'Show Earnings' : 'Hide Earnings';

  const categoryWord = extraParticipantCategory?.category === 'NEW' ? 'New teacher' : 'Old teacher';
  // A-12: with no live date the created date decides; the server's label names that date.
  const categoryText =
    extraParticipantCategory?.basis === 'PROFILE_CREATED' && extraParticipantCategory?.label
      ? extraParticipantCategory.label
      : categoryWord;

  return (
    <div style={FIELD_STYLES.card}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
          Payment attributes
        </span>
        {can_edit ? (
          <button
            type="button"
            onClick={openEdit}
            style={{
              padding: '4px 12px',
              background: 'var(--surface-card)',
              color: 'var(--brand-primary)',
              border: '1px solid var(--brand-primary)',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Edit
          </button>
        ) : null}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 10
        }}
      >
        <div>
          <div style={FIELD_STYLES.label}>GST registered</div>
          <div style={FIELD_STYLES.value}>{attrs.is_gst_registered ? 'Yes (GST 5%)' : 'No'}</div>
        </div>
        <div>
          <div style={FIELD_STYLES.label}>Free Pass balance</div>
          <div style={FIELD_STYLES.value}>{attrs.free_pass_balance}</div>
        </div>
      </div>

      {earningsKnown ? (
        <div style={{ marginTop: 10 }}>
          <div style={FIELD_STYLES.label}>Teacher can see Earnings</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={FIELD_STYLES.value}>{earningsOn ? 'Yes' : 'No'}</span>
            {can_edit && !(staffAccount && !earningsOn) ? (
              <button
                type="button"
                onClick={openEarnings}
                style={{
                  padding: '2px 8px',
                  background: 'var(--surface-card)',
                  color: 'var(--brand-primary)',
                  border: '1px solid var(--brand-primary)',
                  borderRadius: 5,
                  fontSize: 11,
                  cursor: 'pointer'
                }}
              >
                {earningsOn ? 'Hide Earnings' : 'Show Earnings'}
              </button>
            ) : null}
          </div>
          <div style={{ ...FIELD_STYLES.label, textTransform: 'none', marginTop: 2 }}>
            {staffAccount && !earningsOn
              ? 'Earnings is not available for staff accounts.'
              : last_change_text(data.earnings_visible_last_change)}
          </div>
        </div>
      ) : null}

      {/* Q24: the category is read-only, derived from Profile Live At; absent on an older backend (compat). */}
      {extraParticipantCategory ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 10,
            marginTop: 10
          }}
        >
          <div>
            <div style={FIELD_STYLES.label}>Profile live date (IST)</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={FIELD_STYLES.value}>
                {format_live_day(extraParticipantCategory.profile_live_at) || 'Not set'}
              </span>
              {can_edit ? (
                <button
                  type="button"
                  onClick={openLiveEdit}
                  style={{
                    padding: '2px 8px',
                    background: 'var(--surface-card)',
                    color: 'var(--brand-primary)',
                    border: '1px solid var(--brand-primary)',
                    borderRadius: 5,
                    fontSize: 11,
                    cursor: 'pointer'
                  }}
                >
                  Edit live date
                </button>
              ) : null}
            </div>
          </div>
          <div>
            <div style={FIELD_STYLES.label}>Teacher category</div>
            <div style={FIELD_STYLES.value}>{categoryText}</div>
            <div style={{ ...FIELD_STYLES.label, textTransform: 'none', marginTop: 2 }}>
              Live on or before 8 Jun 2026: Old. From 9 Jun 2026: New. No live date: the profile
              created date decides; neither: Old. Decides extra-participant pay.
            </div>
          </div>
        </div>
      ) : null}

      <CustomModal show={!!earningsForm} close={closeEarnings}>
        {earningsForm ? (
          <div style={{ minWidth: 'min(420px, 86vw)', padding: '4px 6px' }}>
            <h4 style={{ color: 'var(--brand-primary)', fontSize: 14, marginBottom: 12 }}>
              {earningsForm.visible
                ? 'Show Earnings to this teacher?'
                : 'Hide Earnings from this teacher?'}
            </h4>
            <div style={{ ...FIELD_STYLES.label, textTransform: 'none', margin: '0 0 10px' }}>
              {earningsForm.visible
                ? 'The teacher will see the Earnings page in the teacher app.'
                : 'The Earnings page will be hidden in the teacher app.'}{' '}
              No payment changes.
            </div>
            <label style={FIELD_STYLES.fieldLabel}>
              Reason <span style={{ color: 'var(--state-error)' }}>*</span> (audit trail)
              <textarea
                value={earningsForm.reason}
                onChange={(e) => setEarningsForm({ ...earningsForm, reason: e.target.value })}
                placeholder="e.g. Earnings pilot, batch 1"
                style={{ ...FIELD_STYLES.input, minHeight: 56 }}
              />
            </label>
            {modalError ? (
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--state-error)' }}>
                {modalError}
              </div>
            ) : null}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <button
                type="button"
                onClick={closeEarnings}
                disabled={saving}
                style={{
                  padding: '6px 14px',
                  border: '1px solid var(--border-default)',
                  background: 'var(--surface-card)',
                  borderRadius: 6,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  color: 'var(--text-secondary)'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveEarnings}
                disabled={saving}
                style={{
                  padding: '6px 14px',
                  border: 'none',
                  background: 'var(--brand-primary)',
                  color: 'var(--text-inverse)',
                  borderRadius: 6,
                  fontWeight: 600,
                  cursor: saving ? 'not-allowed' : 'pointer'
                }}
              >
                {saving ? 'Saving…' : earningsVerb}
              </button>
            </div>
          </div>
        ) : null}
      </CustomModal>

      <CustomModal show={!!liveForm} close={closeLiveEdit}>
        {liveForm ? (
          <div style={{ minWidth: 'min(420px, 86vw)', padding: '4px 6px' }}>
            <h4 style={{ color: 'var(--brand-primary)', fontSize: 14, marginBottom: 12 }}>
              Edit profile live date
            </h4>
            <label style={FIELD_STYLES.fieldLabel}>
              Profile live date (India date)
              <input
                type="date"
                max={today_ist()}
                value={liveForm.profile_live_at}
                disabled={liveForm.clear}
                onChange={(e) => setLiveForm({ ...liveForm, profile_live_at: e.target.value })}
                style={FIELD_STYLES.input}
              />
            </label>
            {extraParticipantCategory?.profile_live_at ? (
              <label
                style={{
                  ...FIELD_STYLES.fieldLabel,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  marginTop: 6
                }}
              >
                <input
                  type="checkbox"
                  checked={liveForm.clear}
                  onChange={(e) => setLiveForm({ ...liveForm, clear: e.target.checked })}
                />
                Clear the live date (the profile created date then decides)
              </label>
            ) : null}
            <div style={{ ...FIELD_STYLES.label, textTransform: 'none', margin: '4px 0 10px' }}>
              Open months are recalculated.
            </div>
            <label style={FIELD_STYLES.fieldLabel}>
              Reason <span style={{ color: 'var(--state-error)' }}>*</span> (audit trail)
              <textarea
                value={liveForm.reason}
                onChange={(e) => setLiveForm({ ...liveForm, reason: e.target.value })}
                placeholder="e.g. Ops sheet: profile went live 9 Jun 2026"
                style={{ ...FIELD_STYLES.input, minHeight: 56 }}
              />
            </label>
            {modalError ? (
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--state-error)' }}>
                {modalError}
              </div>
            ) : null}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <button
                type="button"
                onClick={closeLiveEdit}
                disabled={saving}
                style={{
                  padding: '6px 14px',
                  border: '1px solid var(--border-default)',
                  background: 'var(--surface-card)',
                  borderRadius: 6,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  color: 'var(--text-secondary)'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveLiveDate}
                disabled={saving}
                style={{
                  padding: '6px 14px',
                  border: 'none',
                  background: 'var(--brand-primary)',
                  color: 'var(--text-inverse)',
                  borderRadius: 6,
                  fontWeight: 600,
                  cursor: saving ? 'not-allowed' : 'pointer'
                }}
              >
                {saving ? 'Saving…' : 'Save date'}
              </button>
            </div>
          </div>
        ) : null}
      </CustomModal>

      {/* always mounted: CustomModal only detaches its keydown listener on a show true→false flip */}
      <CustomModal show={editing && !!form} close={closeModal}>
        {form ? (
          <div style={{ minWidth: 'min(460px, 86vw)', padding: '4px 6px' }}>
            <h4 style={{ color: 'var(--brand-primary)', fontSize: 14, marginBottom: 12 }}>
              Edit payment attributes
            </h4>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 10,
                marginBottom: 12
              }}
            >
              <label style={FIELD_STYLES.fieldLabel}>
                GST registered
                <select
                  value={form.is_gst_registered}
                  onChange={(e) => setForm({ ...form, is_gst_registered: e.target.value })}
                  style={FIELD_STYLES.input}
                >
                  <option value={0}>No</option>
                  <option value={1}>Yes (GST 5%)</option>
                </select>
              </label>
              <label style={FIELD_STYLES.fieldLabel}>
                Free Pass balance (0–12)
                <input
                  type="number"
                  min={0}
                  max={12}
                  step={1}
                  value={form.free_pass_balance}
                  onChange={(e) => setForm({ ...form, free_pass_balance: e.target.value })}
                  style={FIELD_STYLES.input}
                />
              </label>
            </div>
            <label style={FIELD_STYLES.fieldLabel}>
              Reason <span style={{ color: 'var(--state-error)' }}>*</span> (audit trail)
              <textarea
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="e.g. GST certificate received / seeded from Ops sheet"
                style={{ ...FIELD_STYLES.input, minHeight: 56 }}
              />
            </label>
            {modalError ? (
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--state-error)' }}>
                {modalError}
              </div>
            ) : null}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <button
                type="button"
                onClick={closeModal}
                disabled={saving}
                style={{
                  padding: '6px 14px',
                  border: '1px solid var(--border-default)',
                  background: 'var(--surface-card)',
                  borderRadius: 6,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  color: 'var(--text-secondary)'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                style={{
                  padding: '6px 14px',
                  border: 'none',
                  background: 'var(--brand-primary)',
                  color: 'var(--text-inverse)',
                  borderRadius: 6,
                  fontWeight: 600,
                  cursor: saving ? 'not-allowed' : 'pointer'
                }}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        ) : null}
      </CustomModal>
    </div>
  );
};

export default PaymentAttributes;
